import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { BoundaryData } from "../contexts/DataContext";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

interface MapBoundaryLineProps {
  boundary: BoundaryData;
  editing: boolean;
}

/**
 * MapBoundary component renders a Google Maps Polyline with boundary styling
 */
export default function MapBoundaryLine({
  boundary,
  editing = false,
}: MapBoundaryLineProps) {
  const map = useMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  const navigate = useNavigateKeepSearchParams(["return_to"]);

  useEffect(() => {
    if (!map || !boundary.coords || boundary.coords.length < 2) return;

    // Create the polyline path from coordinates
    const path = boundary.coords.map(
      (coord) => new google.maps.LatLng(coord[0], coord[1]),
    );

    const strokeColor = boundary.color || "#FF0000";

    // Create polyline with arrow styling
    const polyline = new google.maps.Polyline({
      path: path,
      geodesic: true,
      strokeColor: strokeColor,
      strokeOpacity: 1.0,
      strokeWeight: 3,
      draggable: false,
      editable: false,
      map: map,
    });

    polylineRef.current = polyline;

    // Add click handler for interaction
    google.maps.event.addListener(polyline, "click", () => {
      if (editing) {
        navigate(`edit/boundary`);
      }
    });

    // Cleanup function
    return () => {
      if (polylineRef.current) {
        google.maps.event.clearListeners(polylineRef.current, "click");

        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, editing, boundary.color, boundary.coords, navigate]);

  // This component doesn't render any DOM elements directly
  // The polyline is rendered directly on the Google Map
  return null;
}
